import type { PaymentMethod, SyncOperationInput, SyncOperationType } from '@cuito/types';

/**
 * 离线操作的**唯一**投影（契约 §18.2、`05-ui-design-system.md` §6.8 实现要点 1）。
 *
 * ## 为什么单独一个文件
 *
 * 收银端离线时做的每一件事（接单 / 挑拣 / 缺货 / 收款 / 交付 / 取消）最终都要变成
 * `POST /orders/sync` 请求体里的一个 `SyncOperationInput`。这份「界面动作 → 补传载荷」
 * 的映射只允许有一处：写在页面里就会出现第二份（离线队列页、面板、订单页各写各的），
 * 而它们的 `type` 字符串与 `occurredAt` 口径迟早漂移 —— 漂移的症状是**重放顺序错了**
 * （挑拣先于接单）或**操作类型错了**（`CANCEL_ORDER` 被当成 `COMPLETE_ORDER` 重放），
 * 两者都不会报错，只会把订单改成一个错误的终态。
 *
 * ## `opId` 必须是 UUID（不是自增序号）
 *
 * 契约 §18.2 的校验表逐字 `@IsUUID()`：自增序号（或 `op-1` 这种拼出来的串）会让整个
 * `operations[]` 被全局 `ValidationPipe` 判 400，而这**不是**服务端的问题 ——
 * 它是客户端把一个内部计数器当成了幂等键。因此 `nextOpId()` 只做一件事：要一个 UUID。
 * 它同时是「同一次离线会话内不重复排队」的键（服务端只回声、不落库，见 §18.2 的 A-P9-11）。
 *
 * ## `occurredAt` 在**入队时**取，不在同步时取
 *
 * 服务端按 `occurredAt` 升序重放（§18.2 的语义）。若把时间戳挪到「同步那一刻」再取，
 * 同一次离线会话里的六条操作会拿到几乎相同的时间戳，顺序退化成数组顺序 ——
 * 而数组顺序在重试、部分失败、逐条上传之后**不再等于**店员操作的顺序。
 */
export function nextOpId(): string {
  return globalThis.crypto.randomUUID();
}

function base(type: SyncOperationType, orderNo: string): SyncOperationInput {
  return { opId: nextOpId(), type, orderNo, occurredAt: new Date().toISOString() };
}

/**
 * 离线收款没勾核对时**拒绝**构造这条操作（契约 §12.2、R-02）。
 *
 * 服务端的对应判据是 422 `CUSTOMER_VERIFICATION_REQUIRED`（`origin = "OFFLINE_SCAN"` 且
 * `verifiedWithCustomer !== true`）。前端把它**提前到投影这一层**：错误的东西根本进不了
 * 队列，也就不会在一次断网重启之后被重放出去。界面上的「未勾选 ⇒ 禁用提交」
 * （§6.4 规则 5b）是第一道，这里是第二道 —— 两道看的是同一个布尔值。
 */
export const CUSTOMER_VERIFICATION_REQUIRED = 'CUSTOMER_VERIFICATION_REQUIRED';

export const offlineActions = {
  accept: (orderNo: string): SyncOperationInput => base('ACCEPT_ORDER', orderNo),

  /**
   * 挑拣是**累计值**语义（与 `POST /orders/:id/pick` 逐字相同，契约 §12.2）：
   * 发的是「这一行现在一共挑了多少」，不是增量。
   */
  pick: (orderNo: string, lines: { orderLineId: string; pickedQty: number }[]): SyncOperationInput => ({
    ...base('PICK_LINES', orderNo),
    lines,
  }),

  /** 缺货同样是累计值，且 `shortQty` 单调不减（契约 §12.2） */
  short: (orderNo: string, shortLines: { orderLineId: string; shortQty: number }[]): SyncOperationInput => ({
    ...base('SHORT_LINES', orderNo),
    shortLines,
  }),

  /**
   * 离线收款（`origin = 'OFFLINE_SCAN'` 的那一条路）。
   *
   * `verified !== true` 直接抛（见 `CUSTOMER_VERIFICATION_REQUIRED`）：`note` 是「已与顾客核对」
   * 这句人话的载体，服务端把它原样写进收款备注（`orders-sync.service.ts` 的 `confirmPaymentOp`
   * 读 `op.note`）—— 没有核对就没有这句备注，也不该有这条操作。
   */
  confirmPayment: (orderNo: string, method: PaymentMethod, verified: boolean): SyncOperationInput => {
    if (verified !== true) throw new Error(CUSTOMER_VERIFICATION_REQUIRED);
    return { ...base('CONFIRM_PAYMENT', orderNo), method, note: '离线取单：已与顾客核对' };
  },

  complete: (orderNo: string): SyncOperationInput => base('COMPLETE_ORDER', orderNo),

  /** 取消必须带理由（服务端把它写进 `cancelReason`；空理由的取消在账本上是一笔查不清的账） */
  cancel: (orderNo: string, reason: string): SyncOperationInput => ({ ...base('CANCEL_ORDER', orderNo), reason }),
};
